/* eslint-disable no-param-reassign */
import cn from 'classnames';
import LdsIcon from '../LdsIcon/index.jsx';
import {
  useState, useEffect, useRef, useMemo, useId
} from 'react';
import {
  filterAndMapComponents,
  centerElements,
  calculateYPosition,
  resetElements
} from './util.js';
import PropTypes from 'prop-types';
import useClickOutside from '../../hooks/useClickOutside.js';
import useHover from '../../hooks/useHover.js';
import useTooltipVisibility from './useTooltipVisibility.js';
import useEventListener from '../../hooks/useEventListener.js';

function LdsTooltip({
  children,
  width = 400,
  openDelay = 400,
  closeDelay = 400,
  hideIcon = false,
  position = 'top',
  edgePadding = 16,
  constrainToParent = false,
  accessibilityLabels = {
    closeButton: 'Close tooltip',
    moreInfoIcon: 'More information',
  },
  className,
  ...rest
}) {
  const [isPersistent, setIsPersistent] = useState(false);
  const [renderTooltip, setRenderTooltip] = useState(false);
  const [adjustedWidth, setAdjustedWidth] = useState(width);
  const { isVisible, showTooltip, hideTooltip } = useTooltipVisibility();

  const wrapperRef = useClickOutside(hideTooltip);
  const [tooltipRef, toolTipIsHovered, imperativeSetTooltipHovered] = useHover();
  const [tooltipDescriptionRef, tooltipDescriptionHovered] = useHover();
  const tooltipArrowRef = useRef(null);
  const isHovered = toolTipIsHovered || tooltipDescriptionHovered;
  const uniqueId = useId(); // TODO: This is a react 18 feature. Remove for older support
  const tooltipId = `lds-tooltip${uniqueId}`;
  useEventListener(tooltipDescriptionRef, 'transitionend', handleTransitionEnd);

  function handleTransitionEnd(event) {
    // if tooltip is hovered after transition end, don't hide it
    if (event.propertyName === 'opacity' && !isVisible && !toolTipIsHovered) {
      setRenderTooltip(false);
      setIsPersistent(false);
    }
  }

  // calculated position needs to be preserved between renders
  const tooltipPosition = useMemo(
    () => calculateYPosition(position, renderTooltip, tooltipDescriptionRef),
    [renderTooltip, tooltipDescriptionRef.current]
  );

  useEffect(() => {
    // Calculate adjusted width based on viewport and edgePadding
    const adjustedViewportWidth = window.innerWidth - 2 * edgePadding;
    const newAdjustedWidth = Math.min(width, adjustedViewportWidth);
    setAdjustedWidth(newAdjustedWidth);
  }, [edgePadding, width]);

  useEffect(() => {
    if (isPersistent) {
      showTooltip();
    } else {
      hideTooltip();
    }
  }, [isPersistent]);

  useEffect(() => {
    if (renderTooltip) {
      centerElements({
        tooltip: tooltipRef.current,
        tooltipDescription: tooltipDescriptionRef.current,
        toolTipWrapper: wrapperRef.current.parentElement,
        edgePadding,
        constrainToParent,
        tooltipArrow: tooltipArrowRef.current,
      });
    } else {
      resetElements({ tooltipDescriptionRef, tooltipArrowRef });
    }
  }, [renderTooltip]);

  function toggleTooltip() {
    hideTooltip();
    setRenderTooltip(true);
    setIsPersistent(!isPersistent);
    if (isPersistent) {
      imperativeSetTooltipHovered(false);
    }
  }

  // manages tooltip visibility based on the delay props
  useEffect(() => {
    let timeout;
    if (isHovered) {
      timeout = setTimeout(showTooltip, openDelay);
    } else {
      timeout = setTimeout(() => {
        if (!isPersistent && !isHovered) hideTooltip();
      }, closeDelay);
    }
    return () => clearTimeout(timeout);
  }, [isHovered, isPersistent, openDelay, closeDelay]);

  // render text and description functions
  function renderTooltipText() {
    return filterAndMapComponents(children, LdsTooltip.Text);
  }

  function renderTooltipDescription() {
    return filterAndMapComponents(children, LdsTooltip.Description);
  }

  return (
    <div style={{ display: 'inline' }} ref={wrapperRef}>
      <span
        id="lds-tooltip"
        ref={tooltipRef}
        className={cn(
          'lds-tooltip',
          className
        )}
        {...rest}
      >
        <span
          id="lds-tooltip-container"
          className="tooltip-container"
          onClick={toggleTooltip}
          onMouseEnter={() => {
            setRenderTooltip(true);
          }}
        >
          <button
            aria-describedby={tooltipId}
            className="lds-button-base tooltip-text"
            onFocus={() => {
              setRenderTooltip(true);
              imperativeSetTooltipHovered(true);
            }}
            onBlur={() => {
              imperativeSetTooltipHovered(false);
            }}
          >
            {renderTooltipText()}
            {!hideIcon && (
              <span className="lds-tooltip-icon">
                <LdsIcon
                  className="tooltip-icon"
                  decorative
                  name="InfoFill"
                  inline
                  style={{
                    fill: `${
                      isHovered && !isPersistent
                        ? 'var(--lds-color-tooltip-icon-hover)'
                        : 'var(--lds-color-tooltip-icon)'
                    } `,
                  }}
                  label={accessibilityLabels.moreInfoIcon}
                />
              </span>
            )}
          </button>
        </span>

        <span
          style={{
            width: `${adjustedWidth}px`,
            display: `${renderTooltip ? 'block' : 'none'}`,
          }}
          id={tooltipId}
          ref={tooltipDescriptionRef}
          data-testid="tooltip-description"
          className={cn(
            'tooltip-description',
            'lds-tooltip-description',
            tooltipPosition,
            {
              'lds-tooltip-enter-to': isVisible,
              'lds-tooltip-leave-to': !isVisible,
            }
          )}
          onClick={() => setIsPersistent(true)}
          onMouseEnter={() => {
            setRenderTooltip(true);
          }}
        >
          {renderTooltipDescription()}
          <span
            className="lds-tooltip-arrow"
            style={{ opacity: 1 }}
            ref={tooltipArrowRef}
          />
          {isPersistent && (
            <button
              id="btnClose"
              data-testid="btnClose"
              onClick={() => {
                hideTooltip();
                imperativeSetTooltipHovered(false);
              }}
              className="lds-button-base tooltip-close"
            >
              <LdsIcon
                role="button"
                name="XCircleFill"
                inline
                style={{
                  fill: 'var(--lds-color-tooltip-icon)',
                }}
                label={accessibilityLabels.closeButton}
              />
            </button>
          )}
        </span>
      </span>
    </div>
  );
}

const Text = ({ children }) => <>{children}</>;
LdsTooltip.Text = Text;

const Description = ({ children }) => <>{children}</>;
LdsTooltip.Description = Description;

LdsTooltip.propTypes = {
  children: PropTypes.node,
  width: PropTypes.number,
  position: PropTypes.oneOf(['top', 'bottom']),
  openDelay: PropTypes.number,
  closeDelay: PropTypes.number,
  hideIcon: PropTypes.bool,
  edgePadding: PropTypes.number,
  constrainToParent: PropTypes.bool,
  accessibilityLabels: PropTypes.shape({
    closeButton: PropTypes.string,
    moreInfoIcon: PropTypes.string,
  }),
};

LdsTooltip.Text.propTypes = {
  children: PropTypes.node,
};

LdsTooltip.Description.propTypes = {
  children: PropTypes.node,
};

export default LdsTooltip;
